/**
 * Module feature-tab planning (UX v0.3 §5 S4b as OVERRIDDEN below, seam S10).
 *
 * **Override — Damien, 2026-08-26.** UX S4b specified an overflow: six tabs
 * visible and the rest collapsed into one "More ▾". Damien ruled that "More"
 * is a UX error that should never have been specified, and that named GROUP
 * dropdowns replace it. A module owner declares which group each tab belongs
 * under (`tab_group`); the shell renders one row of group dropdowns plus any
 * ungrouped tabs. Nothing is hidden behind an unnamed label any more, and
 * nothing is hidden by arithmetic — a person reads "Analytics" and knows what
 * is inside it. S4b is not silently dropped: it is recorded as replaced here.
 *
 * The one S4b guarantee that SURVIVES the override, because the reason for it
 * was never about overflow: **the active tab is always visible**, either as its
 * own tab or as the label of the group it sits in. A person can always see
 * where they are.
 *
 * The planning is arithmetic over a list, so it lives here rather than inside
 * the component, and is tested as such.
 */

import type { MeResponse } from "./api";

export type TabEntry = MeResponse["areas"][number]["modules"][number]["tabs"][number];

/** One item in the rendered bar: a plain tab, or a group of tabs in a dropdown. */
export type TabBarItem =
  | { kind: "tab"; tab: TabEntry }
  | {
      kind: "group";
      /** The group's declared name — what the dropdown trigger reads when idle. */
      label: string;
      /** Members, in `tab_order`. */
      tabs: TabEntry[];
      /** The active tab when it lives in THIS group, else null. */
      activeTab: TabEntry | null;
    };

export interface TabPlan {
  /** False for a 0- or 1-tab module: a bar with one tab is chrome that teaches nothing. */
  render: boolean;
  /** The bar, left to right: tabs and group dropdowns interleaved by impact order. */
  items: TabBarItem[];
}

/**
 * Plan the tab bar.
 *
 * Order comes from the registry's `tab_order` — set by the module owner by
 * expected impact, highest first — and never from this file's opinion (I-5). A
 * group takes the position of its highest-impact member, so grouping never
 * demotes a module's most important feature to the end of the row.
 *
 * A tab is never dropped. Every declared tab is either its own item or a member
 * of exactly one group — a disappearing tab reads as "this feature was taken
 * away", and only the registry's permission filter, upstream of here, removes
 * one.
 */
export function planTabs(tabs: TabEntry[], activeTabKey: string | null): TabPlan {
  const ordered = [...tabs].sort((a, b) => a.tab_order - b.tab_order);

  // UX S4b edge (unchanged by the override): a single-tab module IS its own
  // view. No bar — and the caller still renders the panel.
  if (ordered.length <= 1) return { render: false, items: [] };

  const items: TabBarItem[] = [];
  const groupIndex = new Map<string, number>();

  for (const tab of ordered) {
    const group = tab.tab_group?.trim();
    if (!group) {
      items.push({ kind: "tab", tab });
      continue;
    }
    const at = groupIndex.get(group);
    if (at === undefined) {
      // First member seen wins the group's position — and because `ordered` is
      // sorted, that is its highest-impact member.
      groupIndex.set(group, items.length);
      items.push({ kind: "group", label: group, tabs: [tab], activeTab: null });
      continue;
    }
    (items[at] as Extract<TabBarItem, { kind: "group" }>).tabs.push(tab);
  }

  // A group of ONE is a dropdown holding a single item — strictly worse than
  // the tab itself, which is one click closer and already legible.
  const flattened = items.map((item) =>
    item.kind === "group" && item.tabs.length === 1
      ? ({ kind: "tab", tab: item.tabs[0] } as TabBarItem)
      : item,
  );

  // The surviving S4b guarantee: mark which group holds the active tab, so the
  // trigger can show it and read as selected.
  for (const item of flattened) {
    if (item.kind !== "group") continue;
    item.activeTab = item.tabs.find((t) => t.tab_key === activeTabKey) ?? null;
  }

  return { render: true, items: flattened };
}

/** Which tab a module opens on when the URL names none: the first by impact order. */
export function defaultTabKey(tabs: TabEntry[]): string | null {
  const ordered = [...tabs].sort((a, b) => a.tab_order - b.tab_order);
  return ordered[0]?.tab_key ?? null;
}

/**
 * The tab a native module renders: the URL's tab only when the person holds it.
 *
 * `tabs` is what `/v1/me` returned for this person, so a tab absent from it is
 * one they may not see. Rendering its content anyway (a bookmark, or a URL left
 * open across a view-as) shows a screen whose API will refuse them (bug 3f80402f).
 * Such a URL falls back to the module's default tab, same as a URL with no tab.
 */
export function resolveActiveTabKey(
  tabs: TabEntry[],
  requested: string | undefined,
  // A native module's live preference for the tab it opens on when the URL names none (registry
  // `useNativeModuleTabHints`): Precision Admin opens on its queue only while something waits. Held
  // to the same rule as the URL: a preferred tab the person does not hold falls back to the default.
  preferred?: string | null,
): string | null {
  if (requested && tabs.some((t) => t.tab_key === requested)) return requested;
  // Only a URL naming NO tab takes the preference; a bad or refused tab keeps the documented default.
  if (!requested && preferred && tabs.some((t) => t.tab_key === preferred)) return preferred;
  return defaultTabKey(tabs);
}
